import React from 'react'
import _ from 'lodash'
import { Box, Button } from '@mui/material'
import { useFormikContext } from 'formik'
import { PatientHistoryFormType, INTAKE_NOTE_TYPES } from 'src/participantHistory/constants'
import ConfirmNoteDialog from 'src/participantHistoryForm/form/ConfirmNoteDialog'
import { checkIfSelfPaySource } from 'src/participant/participantUtils'
import { usePatientSource } from 'src/participant/usePatientSource'
import { PatientHistoryEntry } from './types'

const MISSING_DIAGNOSIS_TITLE = 'Diagnosis code missing'
const MISSING_DIAGNOSIS_CONTENTS =
  'Self-pay intake notes can be finalized without a diagnosis/billing code, but the diagnosis is still part of the clinical record. Proceed only if this is intentional.'

type Props = {
  uid: string
  historyType: PatientHistoryFormType
  isSubmitting: boolean
  onSubmit: () => void
}

const ParticipantHistoryFormActions = ({ uid, historyType, isSubmitting, onSubmit }: Props) => {
  const isIntakeNote = INTAKE_NOTE_TYPES.includes(historyType as (typeof INTAKE_NOTE_TYPES)[number])
  if (isIntakeNote) {
    return (
      <Box mt={2}>
        <IntakeNoteConfirmActions uid={uid} isSubmitting={isSubmitting} onSubmit={onSubmit} />
      </Box>
    )
  }

  return (
    <Box mt={2}>
      <Button color="primary" disabled={isSubmitting} type="submit" variant="contained">
        Add
      </Button>
    </Box>
  )
}

const IntakeNoteConfirmActions = ({
  uid,
  isSubmitting,
  onSubmit,
}: {
  uid: string
  isSubmitting: boolean
  onSubmit: () => void
}) => {
  const { values } = useFormikContext<PatientHistoryEntry>()
  const { source, loading } = usePatientSource(uid)

  const billingCodes = _.get(values, ['value', 'BILLING_CODES'], [])
  // Everyone else has the code enforced by the validation schema, which reports it on the field.
  // Self-pay is exempt from that rule, so it gets a confirmation dialog instead. Marking the
  // participant ineligible for the program does not change either path.
  const promptMissingDiagnosisCodes = checkIfSelfPaySource(source) && billingCodes.length === 0

  return (
    <ConfirmNoteDialog
      isSubmitting={isSubmitting}
      onSubmit={onSubmit}
      disabled={loading}
      dialogTitle={promptMissingDiagnosisCodes ? MISSING_DIAGNOSIS_TITLE : undefined}
      dialogContents={promptMissingDiagnosisCodes ? MISSING_DIAGNOSIS_CONTENTS : undefined}
    />
  )
}

export default ParticipantHistoryFormActions
